<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, Ref } from 'vue';
import { useRoute } from 'vue-router';
import { PageName } from '@/api/tracking';
import { DgrLoading } from '@stockmarkteam/donguri-ui';
import AdpDocumentCard from '@/components/common/adp-document-card.vue';
import AdpDocumentInfoDefault from '@/components/common/adp-document/adp-document-info-default.vue';
import AdpDocumentInfo from '@/components/common/adp-document/adp-document-info.vue';
import {
  AdpDocument,
  Comment,
  DocType,
  Group,
  GroupActivitySortType,
  Lang,
  TagSelectItem,
} from '@/types';
import { useGroupMark } from '@/utils/actions/group-mark';
import { useAdpDocumentOptionMenu } from '@/utils/composables/useAdpDocumentOptionMenu';
import { selectArticle } from '@/utils/selectArticle';
import { useGroupActivities, useTeamUsers, useUserInfo } from '@/utils/swr';
import { useEmitter } from '@/utils/vue';
import SwrInfinityScroller from '../../common/swr-infinity-scroller.vue';

const props = defineProps<{
  group: Group;
  activitiesCount: number | undefined;
  isShowArticleCardComment: boolean;
  selectedTags: string[];
  trackingSessionId: string;
  sortType: GroupActivitySortType;
  selectedMemoExistsTypeForApi: boolean | undefined;
  startDate: string | undefined;
  endDate: string | undefined;
  selectedLangForApi: Lang[];
  selectedDocTypesForApi: DocType[];
  searchQueryForCountApi: string;
  selectedPestNewsCategories: string[];
  articleTagList: TagSelectItem[];
}>();

const PAGE_NAME: PageName = 'group_activities';
const route = useRoute();
const { updateTagsAndMemo, removeGroupMark } = useGroupMark({
  trackingBaseData: {
    pageName: 'group',
    pageUrl: route.fullPath,
    feature: 'group_activities',
  },
});

const selectedArticles = ref<AdpDocument[]>([]);
const selectedArticle = computed<AdpDocument | undefined>(() => {
  if (selectedArticles.value.length === 1) {
    return selectedArticles.value[0];
  }
  return undefined;
});

const { data: users } = useTeamUsers();
// 右カラムの共有者というUIのために使用される
// 現在のgroupでgroup markをつけたユーザーを取得して表示するため
const selectedArticleGroupMarkUser = computed(() => {
  const selectedGroupMark = selectedArticles.value[0].group_marks.find(
    gm => gm.group_id === props.group.id,
  );

  return users.value?.users.find(u => {
    return u.id === selectedGroupMark?.creator_id;
  });
});

const hasGroupMark: (article: AdpDocument) => boolean = (
  article: AdpDocument,
) => {
  return (
    article.group_marks.find(gm => gm.group_id === props.group.id) !== undefined
  );
};

const removeDocumentCard = (
  adpDocuments: AdpDocument[],
  id: AdpDocument['id'],
) => {
  const cardIndex = adpDocuments.findIndex(d => d.id === id);
  if (cardIndex >= 0) {
    adpDocuments.splice(cardIndex, 1);
  }
};

const removeGroupMarkWrapper = async (article: AdpDocument) => {
  await removeGroupMark({
    adpDocument: article,
    group: props.group,
  });

  const hasGroupComment = article.comments.some(
    c => c.group_id === props.group.id,
  );
  if (!hasGroupComment) {
    emitter.emit('pagination-item-delete', (items: AdpDocument[]) =>
      removeDocumentCard(items, article.id),
    );
  }
};

const optionMenu = useAdpDocumentOptionMenu(
  { pageName: PAGE_NAME, feature: 'group_activities' },
  { onRemoveGroupMark: removeGroupMarkWrapper },
);

// ADP card を選択した時の処理
const toggleSelected = (e: MouseEvent, item: AdpDocument) => {
  selectedArticles.value = selectArticle(e, item, selectedArticles.value);
};
// ADP card に渡すtracking用のdata
const contentsContext = computed(() => {
  return {
    event_name: 'group_activities_view',
    session_id: props.trackingSessionId,
  } as const;
});

// ADP card が選択されているかどうかを判定する。classのみに使用されている
const isSelected = (item: AdpDocument) => {
  return selectedArticles.value.map(i => i.id).includes(item.id);
};

const { data: myInfo } = useUserInfo();
const hasEditInfoPermission = computed(() =>
  myInfo.value !== undefined || props.group !== undefined
    ? myInfo.value!.role === 'admin' || props.group.is_member
    : false,
);

const emitter = useEmitter();
// 記事情報の更新処理
const articleFilter = (article: AdpDocument, id: AdpDocument['id']) => {
  return article.id === id;
};
const updateArticle = (
  articles: AdpDocument[],
  updatedArticle: AdpDocument,
) => {
  const index = articles.findIndex(c => {
    return c.id === updatedArticle.id;
  });
  if (index >= 0) {
    articles[index].marks = updatedArticle.marks;
    articles[index].memo = updatedArticle.memo;
    articles[index].tags = updatedArticle.tags;
  }
  return articles;
};
const updateArticleInfo = async (article: AdpDocument) => {
  if (!selectedArticle.value) return;

  await updateTagsAndMemo({
    group: props.group,
    adpDocument: selectedArticle.value,
    tags: article.tags,
    memo: article.memo,
  });

  emitter.emit('pagination-items-update', {
    filterFunc: (i: AdpDocument) => articleFilter(i, article.id),
    updateFunc: (items: AdpDocument[]) => updateArticle(items, article),
  });
};

// コメントの削除処理
const deleteComment = (comments: Comment[], id: Comment['id']) => {
  const parentIndex = comments.findIndex(c => c.id === id);
  if (parentIndex >= 0) {
    comments.splice(parentIndex, 1);
  } else {
    comments.forEach(comment => {
      const idx = comment.children?.findIndex(child => {
        return child.id === id;
      });
      if (idx >= 0) {
        comment.children.splice(idx, 1);
      }
    });
  }
};
const handleCommentDelete = (id: Comment['id']) => {
  emitter.emit('pagination-item-delete', (items: Comment[]) =>
    deleteComment(items, id),
  );
};
onMounted(async () => {
  emitter.on('comment-deleted', handleCommentDelete);
});
onUnmounted(() => {
  emitter.off('comment-deleted', handleCommentDelete);
});

const getActivities = (
  pageRef: Ref<number>,
  offsetRef: Ref<number>,
  pageLimit: number,
) => {
  return useGroupActivities(
    props.group.id,
    pageRef,
    offsetRef,
    pageLimit,
    props.sortType,
    props.selectedTags,
    props.selectedMemoExistsTypeForApi,
    props.startDate,
    props.endDate,
    props.selectedLangForApi,
    props.selectedDocTypesForApi,
    props.searchQueryForCountApi,
    props.selectedPestNewsCategories,
  );
};
type ResponseType = {
  activities: (AdpDocument | Comment)[];
  next: number | null;
  page: number;
  tracking_session_id: string | undefined;
};
const dataAccessor = (response?: ResponseType) => {
  return response?.activities;
};
const pageInfoAccessor = (response?: ResponseType) => {
  return response?.page !== undefined && response?.next !== undefined
    ? {
        page: response.page,
        next: response.next,
      }
    : undefined;
};

const handleFetchActivities = (response: ResponseType) => {
  emitter.emit(
    'change-tracking-session-id-for-group-activity',
    response?.tracking_session_id,
  );
  selectedArticles.value = [];
};
</script>

<template>
  <div class="group-activity-content">
    <div class="group-activity-card-list-area">
      <div v-if="activitiesCount === 0">
        <div class="no-activity">
          <div class="content">
            <div class="c-text-regular--m">
              該当するアクティビティはありませんでした。
            </div>
          </div>
          <img src="@/assets/state-prepare-feed-small.png" />
        </div>
      </div>
      <SwrInfinityScroller
        :key="
          [
            sortType,
            group.id,
            selectedTags.join(','),
            selectedMemoExistsTypeForApi,
            startDate,
            endDate,
            selectedLangForApi.join(','),
            selectedDocTypesForApi.join(','),
            searchQueryForCountApi,
            selectedPestNewsCategories.join(','),
          ].join('-')
        "
        :page-limit="10"
        :pagination-func="getActivities"
        :data-accessor="dataAccessor"
        :page-info-accessor="pageInfoAccessor"
        @fetch="handleFetchActivities"
        class="group-activity-card-list"
      >
        <template v-slot="{ items: activities, loading }">
          <template v-for="activity in activities">
            <!-- NOTE: activities は comment or article -->
            <!-- NOTE: urlがあるのはarticleのみ -->
            <div
              v-if="'url' in activity"
              :key="`with-document-${activity.id}`"
              class="group-activity-card"
            >
              <AdpDocumentCard
                width="100%"
                :adp-document="activity"
                :theme-id="activity.theme_id"
                :group="group"
                :show-comment="isShowArticleCardComment"
                :page-name="PAGE_NAME"
                :feature="'group_activities'"
                @click="toggleSelected($event, activity)"
                :enable-hover="!isSelected(activity)"
                :is-selected="isSelected(activity)"
                :option-actions="
                  optionMenu.buildOptionActions(activity, {
                    removeGroupMark: hasGroupMark(activity),
                  })
                "
                @option-action="
                  name => optionMenu.handleOptionAction(name, activity)
                "
                :show-tag-list="true"
                :priority-display-tags="selectedTags"
                :contents-context="contentsContext"
              ></AdpDocumentCard>
            </div>
            <!-- NOTE: commentでかつコメントを表示に設定していた場合に表示する -->
            <template v-else-if="isShowArticleCardComment">
              <div
                :key="`only-comment-${activity.id}`"
                class="group-activity-card"
              >
                <AdpDocumentCard
                  width="100%"
                  :theme-id="activity.theme_id"
                  :comment="activity"
                  :page-name="PAGE_NAME"
                  :feature="'group_activities'"
                  :show-tag-list="true"
                  :priority-display-tags="selectedTags"
                  :contents-context="contentsContext"
                ></AdpDocumentCard>
              </div>
            </template>
          </template>
          <DgrLoading v-if="loading" class="loading-spinner" />
        </template>
      </SwrInfinityScroller>
    </div>
    <div class="side-content">
      <div>
        <AdpDocumentInfo
          class="article-info"
          v-if="selectedArticles.length === 1"
          :editable="hasEditInfoPermission"
          :adp-document="selectedArticles[0]"
          :creator="selectedArticleGroupMarkUser"
          :tag-list="articleTagList"
          @article-info-updated="updateArticleInfo"
          :is-group-view="true"
          :group-id="group.id"
          :page-name="PAGE_NAME"
        ></AdpDocumentInfo>
        <AdpDocumentInfoDefault
          :is-group-view="true"
          :adp-document="selectedArticles"
          :page-name="PAGE_NAME"
          :group-id="group.id"
          v-else
        ></AdpDocumentInfoDefault>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.loading-spinner {
  width: 100%;
  margin: 0 auto;
}

.no-activity {
  display: flex;
  padding: 8px 32px;
  background-color: #fff;
  border: 1px solid $color-gray400;
  border-radius: 4px;
  justify-content: space-between;

  .content {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  img {
    width: 100px;
    height: 100px;
  }
}

.group-activity-card-list-area {
  width: 616px;
}

.group-activity-card-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.side-content {
  box-sizing: border-box;
  position: sticky;
  top: calc(var(--layout-sticky-top-under-tabs, 112px) + 16px);
  width: 316px;
  max-height: 80vh;
  overflow-y: auto;
}

.group-activity-content {
  display: grid;
  grid-template-columns: auto auto;
  grid-gap: 16px;
  justify-content: start;
}
</style>
